import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MaterialIcon } from '@/components/ui/MaterialIcon';
import SEO from '@/components/layout/SEO';

const notFoundSchema = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '404 - Resource Not Found',
    description: 'The requested resource could not be found on startzone.dev.',
});

export default function NotFoundPage() {
    const navigate = useNavigate();

    const handleBackTrace = () => {
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }

        navigate('/dashboard');
    };

    return (
        <div className="relative min-h-[calc(100vh-4rem)] overflow-hidden bg-[#0e0e0e] px-6 py-12 font-['Inter'] text-white selection:bg-[#8cacff] selection:text-[#001f55] md:py-16">
            <SEO schema={notFoundSchema} title="404 — EXECUTION FAILED" />

            <div
                className="pointer-events-none fixed inset-0 z-[100] opacity-[0.24]"
                style={{
                    backgroundImage:
                        'linear-gradient(to bottom, rgba(18, 16, 16, 0) 50%, rgba(0, 0, 0, 0.25) 50%), linear-gradient(90deg, rgba(255, 0, 0, 0.06), rgba(0, 255, 0, 0.02), rgba(0, 0, 255, 0.06))',
                        backgroundSize: '100% 2px, 3px 100%',
                }}
            />

            <div className="pointer-events-none fixed inset-0 overflow-hidden">
                <div className="absolute inset-0 opacity-[0.1]" style={{ backgroundImage: 'radial-gradient(circle, #1f2020 0.5px, transparent 0.5px)', backgroundSize: '40px 40px' }} />
                <div className="absolute left-1/2 top-[26%] h-[34rem] w-[34rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(255,113,108,0.14),transparent_72%)] blur-[140px]" />
                <div className="absolute bottom-[-8rem] right-[-8rem] h-[18rem] w-[18rem] rounded-full bg-[radial-gradient(circle,rgba(0,207,252,0.08),transparent_72%)] blur-[120px]" />

                <div className="absolute left-10 top-10 text-[10px] font-['JetBrains_Mono'] leading-tight text-[#8cacff]/28">
                    [ SYSTEM_STATUS: OFFLINE ]
                    <br />
                    [ KERNEL_PANIC: 0x0000404 ]
                    <br />
                    [ SECTOR_ADDR: 0x48-F1-92 ]
                </div>
                <div className="absolute bottom-10 right-10 text-right text-[10px] font-['JetBrains_Mono'] leading-tight text-[#00cffc]/32">
                    COORD_RECOVERY_FAILURE
                    <br />
                    UNAUTHORIZED_NAV_SEQUENCE
                    <br />
                    CYBER_ZONE // TERMINAL_v4.2.1
                </div>
                <div className="absolute left-4 top-1/2 h-64 w-px -translate-y-1/2 bg-gradient-to-b from-transparent via-[#484848]/18 to-transparent" />
                <div className="absolute right-4 top-1/2 h-64 w-px -translate-y-1/2 bg-gradient-to-b from-transparent via-[#484848]/18 to-transparent" />
            </div>

            <div className="pointer-events-none fixed left-0 top-0 p-4">
                <div className="flex h-24 w-24 flex-col justify-start border-l border-t border-[#484848]/16 p-2">
                    <div className="mb-1 h-2 w-2 bg-[#8cacff]/32" />
                    <div className="h-1 w-12 bg-[#484848]/8" />
                </div>
            </div>

            <div className="pointer-events-none fixed bottom-0 right-0 p-4">
                <div className="flex h-16 w-48 items-end justify-end gap-1 border-b border-r border-[#484848]/16 p-2">
                    <div className="h-4 w-1 bg-[#00cffc]/20" />
                    <div className="h-8 w-1 bg-[#00cffc]/40" />
                    <div className="h-12 w-1 bg-[#00cffc]/60" />
                    <div className="h-6 w-1 bg-[#00cffc]/30" />
                    <span className="ml-2 font-['JetBrains_Mono'] text-[10px] text-[#00cffc]/40">
                        DATA_STREAM_LOST
                    </span>
                </div>
            </div>

            <main className="relative z-10 flex min-h-[calc(100vh-4rem)] flex-col items-center justify-center py-6 text-center">
                <div className="relative mb-10">
                    <h1
                        className="bg-gradient-to-b from-[#ff716c] to-[#9f0519] bg-clip-text font-['Space_Grotesk'] text-[120px] font-black italic leading-none tracking-tighter text-transparent opacity-90 md:text-[200px]"
                        style={{ textShadow: '0 0 10px rgba(255, 113, 108, 0.62)' }}
                    >
                        404
                    </h1>

                    <div className="absolute -right-4 -top-3 bg-[#ff716c] px-3 py-1 font-['JetBrains_Mono'] text-[11px] font-bold text-[#490006] [transform:skewX(-12deg)] md:-right-8 md:-top-4">
                        SYSTEM_CRITICAL
                    </div>

                    <div className="absolute bottom-4 left-1/2 flex w-full -translate-x-1/2 items-center justify-center gap-4">
                        <div className="h-px flex-grow bg-gradient-to-l from-[#ff716c]/45 to-transparent" />
                        <span className="whitespace-nowrap font-['JetBrains_Mono'] text-xs uppercase tracking-[0.3em] text-[#ff716c]">
                            Execution Failed
                        </span>
                        <div className="h-px flex-grow bg-gradient-to-r from-[#ff716c]/45 to-transparent" />
                    </div>
                </div>

                <div className="relative w-full max-w-2xl overflow-hidden bg-[#131313]/96 px-8 py-10 shadow-[0_24px_64px_rgba(0,0,0,0.38)] md:px-10 md:py-12">
                    <div className="absolute left-0 top-0 h-4 w-4 border-l-2 border-t-2 border-[#8cacff]/40" />
                    <div className="absolute right-0 top-0 h-4 w-4 border-r-2 border-t-2 border-[#8cacff]/40" />
                    <div className="absolute bottom-0 left-0 h-4 w-4 border-b-2 border-l-2 border-[#8cacff]/40" />
                    <div className="absolute bottom-0 right-0 h-4 w-4 border-b-2 border-r-2 border-[#8cacff]/40" />

                    <div className="space-y-7">
                        <div className="flex flex-col items-center gap-2">
                            <h2 className="font-['Space_Grotesk'] text-2xl font-black uppercase tracking-[0.12em] text-[#8cacff] md:text-[2rem]">
                                SYSTEM ERROR // 404:
                                <br />
                                RESOURCE_NOT_FOUND
                            </h2>
                            <div className="h-0.5 w-16 bg-[#8cacff]" />
                        </div>

                        <div className="font-['JetBrains_Mono'] text-sm leading-relaxed text-[#acabaa] md:text-base">
                            <p className="mb-4">
                                <span className="text-[#00cffc] opacity-80">admin@cyberzone:~$</span> get --path
                                {' '}
                                /requested/resource
                            </p>
                            <p className="mb-3 text-[#ff716c]">[!] CRITICAL_FAULT: DATA_VOID_DETECTED</p>
                            <p className="text-white">
                                The coordinate you are looking for has been purged or never existed.
                            </p>
                            <p className="mt-5 text-xs italic opacity-45">
                                Logs indicate a de-synch in sector 7G. Re-routing protocols suggested.
                            </p>
                        </div>

                        <div className="flex flex-col items-center justify-center gap-3 pt-5 sm:flex-row">
                            <Link
                                to="/dashboard"
                                className="group relative flex w-full items-center justify-center gap-3 bg-[linear-gradient(135deg,#8cacff,#769dff)] px-8 py-4 font-['Space_Grotesk'] text-sm font-black uppercase tracking-[0.18em] text-[#001f55] shadow-[0_0_22px_rgba(140,172,255,0.2)] transition-all hover:brightness-110 active:scale-[0.985] sm:w-auto"
                            >
                                <MaterialIcon icon="rocket_launch" size={18} fill className="text-[#001f55]" />
                                RE-ROUTE TO DASHBOARD
                                <div className="absolute inset-0 border border-[#8cacff]/35 opacity-0 transition-all duration-300 group-hover:scale-110 group-hover:opacity-100" />
                            </Link>

                            <button
                                type="button"
                                onClick={handleBackTrace}
                                className="flex w-full items-center justify-center gap-2 bg-[#131313] px-8 py-4 font-['Space_Grotesk'] text-sm font-bold uppercase tracking-[0.16em] text-[#acabaa] transition-colors hover:bg-[#1a1b1b] hover:text-[#00cffc] active:scale-[0.985] sm:w-auto"
                            >
                                <MaterialIcon icon="history" size={18} className="text-current" />
                                BACK_TRACE
                            </button>
                        </div>
                    </div>
                </div>

                <div className="mt-12 grid grid-cols-2 gap-8 md:grid-cols-4 md:gap-16">
                    <div className="text-left">
                        <span className="mb-1 block font-['JetBrains_Mono'] text-[10px] uppercase tracking-tighter text-[#8cacff]/60">
                            Trace_ID
                        </span>
                        <span className="block font-['JetBrains_Mono'] text-xs text-white">CZ-8829-XQ</span>
                    </div>
                    <div className="text-left">
                        <span className="mb-1 block font-['JetBrains_Mono'] text-[10px] uppercase tracking-tighter text-[#00cffc]/60">
                            Status
                        </span>
                        <span className="block font-['JetBrains_Mono'] text-xs text-white">VOID_STATE</span>
                    </div>
                    <div className="text-left">
                        <span className="mb-1 block font-['JetBrains_Mono'] text-[10px] uppercase tracking-tighter text-[#d674ff]/60">
                            Signal
                        </span>
                        <span className="block font-['JetBrains_Mono'] text-xs text-white">LOST</span>
                    </div>
                    <div className="text-left">
                        <span className="mb-1 block font-['JetBrains_Mono'] text-[10px] uppercase tracking-tighter text-[#ff716c]/60">
                            Auth_Level
                        </span>
                        <span className="block font-['JetBrains_Mono'] text-xs text-white">GUEST_01</span>
                    </div>
                </div>
            </main>
        </div>
    );
}
